import { useState, useEffect, useRef } from 'react';
import copy from 'copy-to-clipboard';
import {
  useLocation,
  useParams,
  useNavigate,
  generatePath,
} from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '@store';
import {
  SidePanel,
  SidePanelType,
  PermissionEnum,
  PermissionDenied,
  SidePanelLoader,
  ButtonPrimary,
  ButtonPseudo,
  IconCopyTwo,
  formatDate,
  IconClock,
  IconSelected,
  BaseTransaction,
  TransactionTypeEnum,
  IconLoader,
  IconArrowRight,
  IconDown,
  IconUp,
} from '@ui/src';
import { clearIntegrationLog } from './store/slices/info';
import {
  useLazyGetPspIntegrationsLogsByIdQuery,
  useLazyGetPspIntegrationLogTransactionByIdQuery,
} from './store/services';
import { useLazyGetConfigurationDictionariesTransactionTypesQuery } from '../../store/dictionaries/service';

import { BankRoutes } from '../../routes';

import cn from './bank-and-psp.module.css';

const getStatusCodeDescription = (status: number) => {
  let statusDescription = '';

  switch (status) {
    case 200:
      statusDescription = 'OK';
      break;
    case 201:
      statusDescription = 'Created';
      break;
    case 301:
      statusDescription = 'Moved Permanently';
      break;
    case 302:
      statusDescription = 'Found';
      break;
    case 304:
      statusDescription = 'Not Modified';
      break;
    case 400:
      statusDescription = 'Bad Request';
      break;
    case 401:
      statusDescription = 'Unauthorized';
      break;
    case 403:
      statusDescription = 'Forbidden';
      break;
    case 404:
      statusDescription = 'Not Found';
      break;
    case 429:
      statusDescription = 'Too Many Requests';
      break;
    case 500:
      statusDescription = 'Internal Server Error';
      break;
    case 502:
      statusDescription = 'Bad Gateway';
      break;
    case 503:
      statusDescription = 'Service Unavailable';
      break;
    case 504:
      statusDescription = 'Gateway Timeout';
      break;
    default:
      break;
  }

  return statusDescription;
};

const IntegrationLog = () => {
  const requestBodyRef = useRef<HTMLDivElement | null>(null);
  const responseBodyRef = useRef<HTMLDivElement | null>(null);

  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [copyIsSuccess, setCopyIsSuccess] = useState(false);
  const [requestBodyCollapsed, setRequestBodyCollapsed] = useState(true);
  const [responseBodyCollapsed, setResponseBodyCollapsed] = useState(true);
  const [requestBodyWithButton, setRequestBodyWithButton] = useState(false);
  const [responseBodyWithButton, setResponseBodyWithButton] = useState(false);

  const {
    configurationDictionaries: { activePermissions, transactionTypes },
    bankAndPsp: {
      info: { integrationLog },
    },
  } = useAppSelector((state) => state);

  const { bankId, logId } = useParams();
  const { search } = useLocation();

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [getLogById, { isLoading, isFetching }] =
    useLazyGetPspIntegrationsLogsByIdQuery();
  const [
    getTransactionById,
    { isLoading: transactionIsLoading, isFetching: transactionIsFetching },
  ] = useLazyGetPspIntegrationLogTransactionByIdQuery();
  const [getTransactionTypes] =
    useLazyGetConfigurationDictionariesTransactionTypesQuery();

  const onGetTransactionById = async (id: string) => {
    const response = (await getTransactionById(id)) as {
      data: { data: BaseTransaction };
    };

    if (response?.data?.data) {
      const currentTransactionType = transactionTypes?.find(
        (item) => item.id === response?.data?.data?.typeId,
      );

      let link = '';

      if (currentTransactionType?.name === TransactionTypeEnum.INCOMING) {
        link = `/transactions/incoming/${id}`;
      } else if (
        currentTransactionType?.name === TransactionTypeEnum.INTERNAL
      ) {
        link = `/transactions/internal/${id}`;
      } else if (currentTransactionType?.name === TransactionTypeEnum.NOSTRO) {
        link = `/transactions/nostro/${id}`;
      } else if (
        currentTransactionType?.name === TransactionTypeEnum.OUTGOING
      ) {
        link = `/transactions/outgoing/${id}`;
      }

      window.open(link, '_blank');
    }
  };

  const onCopy = () => {
    setCopyIsSuccess(true);

    copy(`
Type: ${
      integrationLog?.types
        ? `${integrationLog?.types?.charAt(0)}${integrationLog?.types
            ?.slice(1)
            .toLowerCase()}`
        : '-'
    }   
Request time: ${
      integrationLog?.requestTime
        ? formatDate(integrationLog?.requestTime, true, true)
        : '-'
    }
Response time: ${
      integrationLog?.responseTime
        ? formatDate(integrationLog?.responseTime, true, true)
        : '-'
    }
Status: ${
      integrationLog?.statusCode
        ? `${integrationLog?.statusCode} ${getStatusCodeDescription(
            integrationLog?.statusCode,
          )}`
        : '-'
    }  
Bank: ${
      integrationLog?.pspPartner ? integrationLog?.pspPartner?.companyName : '-'
    }    
Method: ${integrationLog?.eventType || '-'}
URL: ${integrationLog?.url || '-'}
Latency: ${
      integrationLog?.requestTime && integrationLog?.responseTime
        ? `${
            new Date(integrationLog?.responseTime).getTime() -
            new Date(integrationLog?.requestTime).getTime()
          } ms`
        : '-'
    }
Integration type: ${integrationLog?.connectionType || '-'}
Transaction ID: ${integrationLog?.transactionId || '-'}
Request body:
${
  integrationLog?.requestBody
    ? JSON.stringify(
        integrationLog?.requestBody.includes('{') &&
          integrationLog?.requestBody.charAt(0) === '{'
          ? JSON.parse(integrationLog?.requestBody)
          : integrationLog?.requestBody,
        null,
        2,
      )
    : ''
}
Response body:
${
  integrationLog?.responseBody
    ? JSON.stringify(
        integrationLog?.responseBody.includes('{') &&
          integrationLog?.responseBody.charAt(0) === '{'
          ? JSON.parse(integrationLog?.responseBody)
          : integrationLog?.responseBody,
        null,
        2,
      )
    : ''
}
    `);

    setTimeout(() => {
      setCopyIsSuccess(false);
    }, 2000);
  };

  const handleClose = () => {
    dispatch(clearIntegrationLog());
    setPanelIsOpen(false);
    setCopyIsSuccess(false);

    setTimeout(() => {
      navigate(
        `${generatePath(BankRoutes.BANK_AND_PSP_INTEGRATION_LOGS, {
          bankId: bankId as string,
        })}${search}`,
      );
    }, 100);
  };

  useEffect(() => {
    setPanelIsOpen(true);
  }, []);

  useEffect(() => {
    if (logId && bankId) {
      getLogById({ id: bankId, logId });
    }
  }, [logId, bankId]);

  useEffect(() => {
    if (!transactionTypes) {
      getTransactionTypes();
    }
  }, [transactionTypes]);

  useEffect(() => {
    if (requestBodyRef && requestBodyRef?.current && integrationLog) {
      if (requestBodyRef?.current?.clientHeight > 220) {
        setRequestBodyWithButton(true);
      }
    }
  }, [requestBodyRef, integrationLog]);

  useEffect(() => {
    if (responseBodyRef && responseBodyRef?.current && integrationLog) {
      if (responseBodyRef?.current?.clientHeight > 220) {
        setResponseBodyWithButton(true);
      }
    }
  }, [responseBodyRef, integrationLog]);

  const getTransactionIsAvailable = Boolean(
    activePermissions?.includes(PermissionEnum.TRANSACTIONS_NOSTRO_LIST) ||
    activePermissions?.includes(PermissionEnum.TRANSACTIONS_INCOMING_LIST) ||
    activePermissions?.includes(PermissionEnum.TRANSACTIONS_INTERNAL_LIST) ||
    activePermissions?.includes(PermissionEnum.TRANSACTIONS_OUTGOING_LIST),
  );

  let content = <div />;

  if (
    !activePermissions?.includes(
      PermissionEnum.PSP_INTEGRATION_LOGS_LIST_BY_PSP_PARTNER,
    )
  ) {
    content = <PermissionDenied />;
  } else if (
    activePermissions?.includes(
      PermissionEnum.PSP_INTEGRATION_LOGS_LIST_BY_PSP_PARTNER,
    )
  ) {
    if (isLoading || isFetching) {
      content = <SidePanelLoader />;
    } else {
      let responseTimeClassName = '';
      let statusCodeClassName = '';

      if (integrationLog?.requestTime && integrationLog?.responseTime) {
        const difference =
          new Date(integrationLog?.responseTime).getTime() -
          new Date(integrationLog.requestTime).getTime();

        if (difference >= 1500 && difference <= 2500) {
          responseTimeClassName = cn.yellowIcon;
        } else if (difference > 2500) {
          responseTimeClassName = cn.redIcon;
        }
      } else if (!integrationLog?.responseTime) {
        responseTimeClassName = cn.redIcon;
      }

      if (integrationLog?.statusCode) {
        if (integrationLog?.statusCode?.toString()?.charAt(0) === '2') {
          statusCodeClassName = cn.successStatus;
        } else if (integrationLog?.statusCode?.toString()?.charAt(0) === '3') {
          statusCodeClassName = cn.redirectStatus;
        } else if (integrationLog?.statusCode?.toString()?.charAt(0) === '4') {
          statusCodeClassName = cn.errorStatusCode;
        } else if (integrationLog?.statusCode?.toString()?.charAt(0) === '5') {
          statusCodeClassName = cn.internalErrorStatusCode;
        }
      }

      content = (
        <div className={cn.integrationLogContent}>
          <div className={cn.integrationLogRow}>
            <div className={cn.integrationLogRowTitle}>Method</div>
            <div className={cn.integrationLogRowValue}>
              {integrationLog?.eventType ? (
                <div className={cn.eventTypeWrapper}>
                  <div className={cn.eventType}>
                    {integrationLog?.eventType}
                  </div>
                </div>
              ) : (
                '-'
              )}
            </div>
          </div>
          <div className={cn.integrationLogRow}>
            <div className={cn.integrationLogRowTitle}>Type</div>
            <div className={cn.integrationLogRowValue}>
              {integrationLog?.types
                ? `${integrationLog?.types.charAt(0)}${integrationLog?.types
                    .slice(1)
                    .toLowerCase()}`
                : '-'}
            </div>
          </div>
          <div className={cn.integrationLogRow}>
            <div className={cn.integrationLogRowTitle}>URL</div>
            <div className={cn.integrationLogRowValue}>
              {integrationLog?.url || '-'}
            </div>
          </div>
          <div className={cn.integrationLogRow}>
            <div className={cn.integrationLogRowTitle}>Request Time</div>
            <div className={cn.integrationLogRowValue}>
              {integrationLog?.requestTime
                ? formatDate(integrationLog?.requestTime, true, true)
                : '-'}
            </div>
          </div>
          <div className={cn.integrationLogRow}>
            <div className={cn.integrationLogRowTitle}>Response Time</div>
            <div className={cn.integrationLogRowValue}>
              {integrationLog?.responseTime
                ? formatDate(integrationLog?.responseTime, true, true)
                : '-'}
            </div>
          </div>
          <div className={cn.integrationLogRow}>
            <div className={cn.integrationLogRowTitle}>Latency</div>
            <div className={cn.integrationLogRowValue}>
              <div className={cn.integrationLogLatency}>
                <IconClock
                  width={16}
                  height={16}
                  className={responseTimeClassName}
                />
                {integrationLog?.responseTime && integrationLog?.requestTime
                  ? `${
                      new Date(integrationLog?.responseTime).getTime() -
                      new Date(integrationLog?.requestTime).getTime()
                    } ms`
                  : '-'}
              </div>
            </div>
          </div>
          <div className={cn.integrationLogRow}>
            <div className={cn.integrationLogRowTitle}>Status</div>
            <div className={cn.integrationLogRowValue}>
              <div className={cn.integrationLogStatusWrapper}>
                <div
                  className={`${cn.integrationLogStatus} ${statusCodeClassName}`}
                >
                  {integrationLog?.statusCode}
                </div>
                <div className={cn.integrationLogStatusDescriotion}>
                  {getStatusCodeDescription(
                    integrationLog?.statusCode as number,
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className={cn.integrationLogRow}>
            <div className={cn.integrationLogRowTitle}>Integration type</div>
            <div className={cn.integrationLogRowValue}>
              {integrationLog?.connectionType || ''}
            </div>
          </div>
          <div className={cn.integrationLogRow}>
            <div className={cn.integrationLogRowTitle}>Transaction ID</div>
            <div className={cn.integrationLogRowValue}>
              <button
                type="button"
                className={`${cn.transactionIdLink} ${getTransactionIsAvailable ? cn.transactionIdLinkActive : ''}`}
                onClick={() => {
                  if (
                    getTransactionIsAvailable &&
                    integrationLog?.transactionId &&
                    transactionTypes
                  ) {
                    onGetTransactionById(integrationLog?.transactionId);
                  }
                }}
              >
                {integrationLog?.transactionId || '-'}
                {getTransactionIsAvailable &&
                  integrationLog?.transactionId &&
                  transactionTypes && (
                    <>
                      {transactionIsLoading || transactionIsFetching ? (
                        <IconLoader width={14} height={14} />
                      ) : (
                        <IconArrowRight width={14} height={11} />
                      )}
                    </>
                  )}
              </button>
            </div>
          </div>
          <div className={cn.integrationLogResponseBody}>
            <div className={cn.integrationLogResponseBodyTitle}>
              Request body
            </div>
            <div
              className={`${cn.integrationLogResponseBodyContent} ${requestBodyWithButton && requestBodyCollapsed ? cn.maxHeight : ''}`}
              ref={requestBodyRef}
            >
              {integrationLog?.requestBody && (
                <pre>
                  {integrationLog?.requestBody.includes('{') &&
                  integrationLog?.requestBody.charAt(0) === '{' ? (
                    JSON.stringify(
                      integrationLog?.requestBody.includes('{') &&
                        integrationLog?.requestBody.charAt(0) === '{'
                        ? JSON.parse(integrationLog?.requestBody)
                        : integrationLog?.requestBody,
                      null,
                      2,
                    )
                  ) : (
                    <span className={cn.notFormattedBody}>
                      {integrationLog?.requestBody}
                    </span>
                  )}
                </pre>
              )}
              {requestBodyWithButton && (
                <div className={cn.collapsedButtonWrapper}>
                  <button
                    type="button"
                    className={cn.collapsedButton}
                    onClick={() =>
                      setRequestBodyCollapsed(!requestBodyCollapsed)
                    }
                  >
                    Show {requestBodyCollapsed ? 'more' : 'less'}
                    {requestBodyCollapsed ? <IconDown /> : <IconUp />}
                  </button>
                </div>
              )}
            </div>

            {!integrationLog?.requestBody && (
              <div className={cn.integrationLogResponseBodyEmpty}>
                No request body yet
              </div>
            )}
          </div>
          <div className={cn.integrationLogResponseBody}>
            <div className={cn.integrationLogResponseBodyTitle}>
              Response body
            </div>
            <div
              className={`${cn.integrationLogResponseBodyContent} ${responseBodyWithButton && responseBodyCollapsed ? cn.maxHeight : ''}`}
              ref={responseBodyRef}
            >
              {integrationLog?.responseBody && (
                <pre>
                  {integrationLog?.responseBody.includes('{') &&
                  integrationLog?.responseBody.charAt(0) === '{' ? (
                    JSON.stringify(
                      integrationLog?.responseBody.includes('{') &&
                        integrationLog?.responseBody.charAt(0) === '{'
                        ? JSON.parse(integrationLog?.responseBody)
                        : integrationLog?.responseBody,
                      null,
                      2,
                    )
                  ) : (
                    <span className={cn.notFormattedBody}>
                      {integrationLog?.responseBody}
                    </span>
                  )}
                </pre>
              )}
              {responseBodyWithButton && (
                <div className={cn.collapsedButtonWrapper}>
                  <button
                    type="button"
                    className={cn.collapsedButton}
                    onClick={() =>
                      setResponseBodyCollapsed(!responseBodyCollapsed)
                    }
                  >
                    Show {responseBodyCollapsed ? 'more' : 'less'}
                    {responseBodyCollapsed ? <IconDown /> : <IconUp />}
                  </button>
                </div>
              )}
            </div>

            {!integrationLog?.responseBody && (
              <div className={cn.integrationLogResponseBodyEmpty}>
                No response body yet
              </div>
            )}
          </div>
        </div>
      );
    }
  }

  return (
    <>
      <SidePanel
        isOpen={panelIsOpen}
        type={SidePanelType.Accounts}
        onClose={handleClose}
        title="Integration Log"
        sidepanelExpandedKey="bank-internal-log-panel"
        footer={
          <div className={cn.integrationLogControls}>
            <ButtonPrimary
              text="Copy full log"
              icon={<IconCopyTwo />}
              className={cn.copyLogButton}
              onClick={onCopy}
              disabled={isLoading || isFetching}
              bold
            />
            <ButtonPseudo text="Close" onClick={handleClose} bold />
          </div>
        }
        closeOnClickOutside
      >
        {content}
      </SidePanel>
      {copyIsSuccess && (
        <div className={cn.copySuccessHint}>
          <IconSelected width={18} height={18} /> Log copied
        </div>
      )}
    </>
  );
};

export default IntegrationLog;
